/**
 * SSR entry point — invoked by `scripts/prerender.tsx` for each route.
 *
 * Per-route flow:
 *   1. `installSsrGlobals()` stubs localStorage / document / React so i18n can
 *      initialise in Node and the classic JSX runtime finds `React` (tsx
 *      esbuild emits bare `React` identifiers; Vite's automatic runtime
 *      doesn't).
 *   2. Render the App inside <HelmetProvider context={helmetContext}> +
 *      <MemoryRouter initialEntries={[url]}> + <PreloadContext.Provider>.
 *   3. `renderToString` returns the body; `helmetContext.helmet.{title,meta,
 *      link,script}.toString()` returns the head pieces.
 *   4. Caller splices them into `dist/<path>/index.html`.
 *
 * Why a fresh `render()` call per route instead of `renderToStaticMarkup`:
 * the App tree has hooks (useLocation in <SEO>, useTranslation in layout)
 * that need React's full reconciler. Static markup would error.
 *
 * Why MemoryRouter (and not StaticRouter): react-router-dom v7 dropped
 * StaticRouter in favour of `createStaticHandler` + `createStaticRouter`,
 * which are data-router APIs designed for a request handler. For an SSG
 * pass with a known single URL, MemoryRouter + initialEntries is simpler
 * and avoids pulling in the data-router dependencies.
 *
 * @see scripts/prerender.tsx — consumes render() per route
 * @see scripts/_ssr-globals.mts — installs the browser stubs
 * @see https://reactrouter.com/api/MemoryRouter — initialEntries prop
 */

import * as React from 'react'
import ReactDOMServer from 'react-dom/server'
import { HelmetProvider, type HelmetServerState } from 'react-helmet-async'
import { MemoryRouter } from 'react-router-dom'
import { installSsrGlobals } from '../scripts/_ssr-globals.mts'
import { parseLocaleFromPath } from './utils/localePath'

/** Preload payload type — the prerender script fetches the full detail row
 *  via the CMS API at build time, then hands it to the page component via
 *  this context. The page's existing `useEffect` fetch is still the runtime
 *  path; preload only short-circuits the initial render. */
export type PreloadKind = 'article' | 'lecture' | 'magazine'

export interface PreloadPayload {
  kind: PreloadKind
  data: unknown
}

const PreloadContext = React.createContext<PreloadPayload | null>(null)

/** Hook consumed by detail pages. Returns null at runtime (CSR); a payload
 *  during prerender when the page is rendered with build-time data. */
export function usePreload(): PreloadPayload | null {
  return React.useContext(PreloadContext)
}

export interface RenderResult {
  /** The rendered React tree as a string. */
  body: string
  /** Head pieces extracted from <Helmet>. Concatenate in document order:
   *    title + meta + link + script  →  injects into <head>. */
  head: {
    title: string
    meta: string
    link: string
    script: string
    /** Raw helmet state, for debugging or per-tag filtering. */
    raw: HelmetServerState
  }
}

/**
 * Render a single route to { body, head }. Side-effects:
 *   - Installs browser globals (idempotent).
 *   - Switches i18n to the locale parsed from `url` (e.g. `/zh-Hans/...` →
 *     `i18n.changeLanguage('zh-Hans')`) so body content using `useTranslation`
 *     / `i18n.language` — news card titles, magazine labels, dates — renders
 *     in the route's locale instead of always zh-Hant. SEO `<title>`,
 *     `<meta description>`, `<link rel=canonical>`, and `<link rel=alternate
 *     hreflang>` are URL-derived (see src/utils/seo.tsx) and were never
 *     affected by the i18n language; switching i18n here is safe.
 *
 * @param url      the route to render, e.g. `/articles/實用溫習策略`
 * @param preload  optional preloaded detail data; ignored for static routes
 */
export async function render(
  url: string,
  preload: PreloadPayload | null = null,
): Promise<RenderResult> {
  // Install browser globals FIRST (localStorage stub, document stub, etc.)
  // before the App's module dependency chain pulls in @/i18n — i18n reads
  // localStorage at module init. Static `import App from './App'` would
  // trigger i18n to evaluate synchronously at module load, before our
  // stubs are in place. Dynamic import defers that until after we set up.
  await installSsrGlobals()
  // Switch i18n to the route's locale BEFORE the App tree evaluates, so
  // `useTranslation().i18n.language` is correct for the very first render.
  // Without this, the module-default `lng: localStorage ?? 'zh-Hant'` sticks
  // (SSR has no localStorage) and every prerendered body is Traditional,
  // even on /zh-Hans/ and /en/ — wait-for-hydration mismatch on the news
  // section. The SEO <title>/<meta>/<link> are URL-driven, so they don't
  // care about i18n.language.
  const locale = parseLocaleFromPath(url)
  const { default: i18n } = await import('./i18n')
  await i18n.changeLanguage(locale)
  const { default: App } = await import('./App')

  const helmetContext: { helmet?: HelmetServerState } = {}

  const tree = (
    <HelmetProvider context={helmetContext}>
      <MemoryRouter initialEntries={[url]}>
        <PreloadContext.Provider value={preload}>
          <App />
        </PreloadContext.Provider>
      </MemoryRouter>
    </HelmetProvider>
  )

  const body = ReactDOMServer.renderToString(tree)
  const head = helmetContext.helmet ?? {
    title: { toString: () => '' },
    meta: { toString: () => '' },
    link: { toString: () => '' },
    script: { toString: () => '' },
  } as unknown as HelmetServerState

  return {
    body,
    head: {
      title: head.title.toString(),
      meta: head.meta.toString(),
      link: head.link.toString(),
      script: head.script.toString(),
      raw: head,
    },
  }
}
